<script setup lang="ts">
import { ref, computed, inject, onMounted } from 'vue';

import { configuratorStateKey } from '../ProductConfiguratorKeys';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import ConfiguratorChevron from '../ConfiguratorChevron.vue';
import DecorationPlacement from './DecorationPlacement.vue';
import DecorationTechnique from './DecorationTechnique.vue';
import DecorationBottomMenu from './DecorationBottomMenu.vue';
import DecorationUpload from './DecorationUpload.vue';
import DecorationSummary from './DecorationSummary.vue';

const { websiteText } = useWebsiteTexts();
const {
  configuratorState,
  updateSelection,
  updateDecorationSteps,
  removeDecoration,
  removeUpload
} = inject(configuratorStateKey);

const emit = defineEmits(['started', 'techniqueVariantSelected']);
const decorator = ref(null);
const toggleState = ref(true);
const decorationSteps = computed(() => configuratorState.value.decorationSteps);
const selections = computed(() => configuratorState.value.selections);

if (typeof window !== 'undefined') {
  toggleState.value = JSON.parse(localStorage.getItem("configurator:decoration-accordion-open-state") || 'true');
}

const props = defineProps({
  variants: {
    type: Array,
    required: true,
  },
  decorations: {
    type: Array,
    required: true,
  },
  outOfStock: {
    type: Boolean,
    default: false,
  },
});

const availableTechniques = computed(() => {
  const selected = configuratorState.value.selections.selectedPlacement;
  if (!selected) return [];
  return props.decorations.find(d => d.systemId === selected)?.techniques || [];
});

function goToStep(step: string) {
  Object.keys(configuratorState.value.decorationSteps).forEach((k) => {
    updateDecorationSteps(k, k === step);
  });
}

function beginDecorating() {
  emit('started');
  goToStep('placeDecoration');
}

function placementSelected(id: string) {
  updateSelection('selectedPlacement', id);
  goToStep('pickTechnique');
}

function placementValueSelected(value: string) {
  updateSelection('selectedPlacementName', value);
}

function techniqueSelected(id: string) {
  updateSelection('selectedTechnique', id);
  updateSelection('desiredWidth', 0);
  updateSelection('desiredHeight', 0);
}

function techniqueVariantSelected(variant) {
  updateSelection('selectedTechniqueVariant', variant.id);
  updateSelection('selectedTechniqueVariantPrices', variant.brackets);
  updateSelection('selectedTechniqueVariantAddons', variant.addons);

  if (variant.colors) {
    updateSelection('selectedTechniqueVariantInputColors', variant.colors);
  }

  emit('techniqueVariantSelected', variant);
}

function desiredWidthUpdated(value: number) {
  updateSelection('desiredWidth', value);
}

function desiredHeightUpdated(value: number) {
  updateSelection('desiredHeight', value);
}

function fileUploaded(file) {
  if (!file) return;
  updateSelection('fileUpload', file);
  goToStep('confirmDecoration');
}

function fileRemoved() {
  removeUpload();
  updateSelection('fileUpload', null);
  goToStep('uploadDecoration');
}

function accordianToggle() {
  toggleState.value = !toggleState.value;
  localStorage.setItem("configurator:decoration-accordion-open-state", JSON.stringify(toggleState.value));
}

function saveDecoration() {
  goToStep('decorationSummary');
}

function removeDecorationData() {
  removeUpload();
  removeDecoration();
  goToStep('initialState');
}
</script>

<template>
  <div :disabled="outOfStock" ref="decorator" class="product-decorator"  id="decoration-anchor">
    <div
      tabindex="0"
      class="product-decorator__title-and-toggle"
      @click="accordianToggle()"
      @keypress.space.prevent="accordianToggle()"
    >
      <p class="product-decorator__title">
        {{ websiteText('decoration__decorate_product').value }}
      </p>
      <ConfiguratorChevron
        :open="toggleState"
      ></ConfiguratorChevron>

    </div>
    <div :open="toggleState" class="product-decorator__content">
      <div
        v-if="decorationSteps.initialState"
        class="product-decorator__default-content"
      >
        <p class="product-decorator__default-title">
          {{ websiteText('decoration__add_decoration').value }}
        </p>
        <div class="product-decorator__default-cta"
          @click="beginDecorating()"
          :disabled="outOfStock"
        >
          <img src="@/static/icons/white_plus.svg">
        </div>
        <div class="product-decorator__out-of-stock-info" v-if="outOfStock">
          {{ websiteText('decoration__out_of_stock').value }}
        </div>
      </div>
      <div class="product-decorator__config-decoration">
        <DecorationPlacement
          :placements="decorations"
          :selected="selections.selectedPlacement"
          v-if="decorationSteps.placeDecoration
             || decorationSteps.pickTechnique"
          @selected="placementSelected"
          @selected-value="placementValueSelected"
        >
        </DecorationPlacement>
        <DecorationTechnique
          :variants="props.variants"
          :techniques="availableTechniques"
          :selected="selections.selectedTechnique"
          :selectedVariant="selections.selectedTechniqueVariant"
          :selectedColors="selections.selectedTechniqueVariantInputColors"
          :selected-width="selections.desiredWidth"
          :selected-height="selections.desiredHeight"
          v-if="decorationSteps.pickTechnique"
          @selected="techniqueSelected"
          @selectedVariant="techniqueVariantSelected"
          @desiredWidthUpdated="desiredWidthUpdated"
          @desiredHeightUpdated="desiredHeightUpdated"
        >
        </DecorationTechnique>
        <DecorationUpload
          :upload="selections.fileUpload"
          v-if="decorationSteps.uploadDecoration
          || decorationSteps.confirmDecoration"
          @upload="fileUploaded"
          @remove="fileRemoved"
        >
        </DecorationUpload>
        <DecorationSummary
          :placements="decorations"
          v-if="decorationSteps.decorationSummary"
          @edit="goToStep"
        >
        </DecorationSummary>
        <DecorationBottomMenu
          @goToStep="goToStep"
          @save="saveDecoration"
          @remove="removeDecorationData"
        >
        </DecorationBottomMenu>
      </div>
    </div>
  </div>
</template>

<style>
  .product-decorator {
    padding: 13px 20px 13px;
    border: 1px solid var(--color-neutrals-50);
    border-radius: 3px;
  }

  .product-decorator__title-and-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    user-select: none;
    cursor: pointer;
  }

  .product-decorator[disabled=true]
  .product-decorator__title-and-toggle {
    opacity: 0.4;
  }

  .product-decorator__title {
    margin: 0;
    font-weight: bold;
    user-select: none;
  }

  .product-decorator__content {
    padding: 13px 0 7px;
    display: none;
  }

  .product-decorator__content[open="true"] {
    display: block; 
  }

  .product-decorator__default-title {
    font-size: 14px;
    line-height: 24px;
    margin: 0 0 8px;
    color: var(--color-text-darker);
    user-select: none;
  }

  .product-decorator__default-cta {
    border-radius: 3px;
    background-color: var(--color-neutrals-100);
    height: 51px;
    width: 51px;
    display: grid;
    place-items: center;
    user-select: none;
    cursor: pointer;
  }

  .product-decorator[disabled=true]
  .product-decorator__default-title,
  .product-decorator[disabled=true]
  .product-decorator__default-cta {
    opacity: 0.4;
    pointer-events: none;
  }

  .product-decorator__out-of-stock-info {
    color: var(--color-feedback-information);
    background-color: rgba(83, 121, 255, 0.04);
    padding: 8px 15px;
    font-size: 14px;
    line-height: 25px;
    margin-top: 20px;
    user-select: none;
  }
</style>
